/**
 * generateTreeHtml — Generates a print-ready Team Structure (org chart) HTML document.
 *
 * Renders the team hierarchy as a styled indented list with role details,
 * assignee names, and status badges. The SVG tree layout from TeamStructureView
 * doesn't translate well to print, so we use a hierarchical list instead.
 *
 * Follows the same window.open() + print pattern as generateBomHtml.ts.
 *
 * Epic: DEV-625
 */

import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
} from '../../api/projectRoles';
import type { SecurityUserResponse } from '../../api/security';
import i18n from '../../i18n/config';

/* Cast to bypass i18next v25 strict 0-arg type signature */
const t = i18n.t.bind(i18n) as (key: string, opts?: Record<string, string | number>) => string;

/* ── HTML escaping ───────────────────────────────────────────── */

function esc(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── Tree node for hierarchy ─────────────────────────────────── */

interface PrintTreeNode {
  slot: ProjectRoleSlotResponse;
  children: PrintTreeNode[];
}

/* ── Build tree from flat slot list ──────────────────────────── */

function buildTree(slots: ProjectRoleSlotResponse[]): PrintTreeNode[] {
  const nodeMap = new Map<string, PrintTreeNode>();
  const roots: PrintTreeNode[] = [];

  // Create nodes
  for (const slot of slots) {
    nodeMap.set(slot.roleSlotGuid.toUpperCase(), { slot, children: [] });
  }

  // Build parent-child relationships
  for (const slot of slots) {
    const node = nodeMap.get(slot.roleSlotGuid.toUpperCase())!;
    if (slot.reportsToSlotGuid) {
      const parent = nodeMap.get(slot.reportsToSlotGuid.toUpperCase());
      if (parent) {
        parent.children.push(node);
        continue;
      }
    }
    roots.push(node);
  }

  // Sort children alphabetically by label
  function sortChildren(node: PrintTreeNode): void {
    node.children.sort((a, b) => a.slot.roleLabel.localeCompare(b.slot.roleLabel));
    node.children.forEach(sortChildren);
  }
  roots.sort((a, b) => a.slot.roleLabel.localeCompare(b.slot.roleLabel));
  roots.forEach(sortChildren);

  return roots;
}

/* ── Render a node and its children recursively ──────────────── */

function renderNode(
  node: PrintTreeNode,
  userMap: Map<string, SecurityUserResponse>,
  ownerGuid: string,
  depth: number,
): string {
  const slot = node.slot;
  const activeAssignments = (slot.assignments ?? []).filter(
    (a) => a.status === 'active' || a.status === 'pending_verification'
  );

  /* Assignee names */
  const assigneeNames = activeAssignments.map((a) => {
    const user = userMap.get(a.userGuid.toUpperCase());
    // DEV-963: SecurityUserResponse uses a single `name` field (also
    // `displayName`), not firstName/lastName. Fall back to displayName, then
    // 'vacant' if both are missing.
    return user ? esc((user.name || user.displayName || '').trim()) : esc(t('projectTeam:structure.vacant'));
  });

  const isVacant = assigneeNames.length === 0;
  const fillStatus = `${slot.slotsFilled}/${slot.slotsNeeded}`;

  /* Phase label */
  const phaseLabel = esc(t(`projectTeam:phases.${slot.phase}`) || slot.phase);

  /* Is owner */
  const isOwner = activeAssignments.some(
    (a) => a.userGuid.toUpperCase() === ownerGuid.toUpperCase()
  );

  let html = `
    <div class="tree-node depth-${Math.min(depth, 4)}" style="margin-inline-start: ${depth * 28}px;">
      <div class="node-header">
        <span class="role-label">${esc(slot.roleLabel)}</span>
        <span class="role-category">${esc(slot.roleCategory)}</span>
        ${isOwner ? '<span class="owner-badge">Owner</span>' : ''}
        <span class="fill-badge ${isVacant ? 'vacant' : 'filled'}">${fillStatus}</span>
      </div>
      <div class="node-body">
        ${isVacant
          ? `<span class="vacant-text">${esc(t('projectTeam:structure.vacant'))}</span>`
          : `<span class="assignee-list">${assigneeNames.join(', ')}</span>`
        }
        <span class="phase-label">${phaseLabel}</span>
      </div>
    </div>`;

  /* Render children */
  if (node.children.length > 0) {
    for (const child of node.children) {
      html += renderNode(child, userMap, ownerGuid, depth + 1);
    }
  }

  return html;
}

/* ── Main generator ──────────────────────────────────────────── */

export function openTreePrintWindow(
  slots: ProjectRoleSlotResponse[],
  userMap: Map<string, SecurityUserResponse>,
  ownerGuid: string,
  projectName: string,
): void {
  const now = new Date();
  const dateStr = now.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const tree = buildTree(slots);

  /* ── Summary stats ─────────────────────────────────────────── */
  const totalSlots = slots.length;
  const totalPositions = slots.reduce((sum, s) => sum + s.slotsNeeded, 0);
  const filledPositions = slots.reduce((sum, s) => sum + s.slotsFilled, 0);
  const openPositions = Math.max(0, totalPositions - filledPositions);

  /* ── Render all tree nodes ─────────────────────────────────── */
  let treeHtml = '';
  for (const root of tree) {
    treeHtml += renderNode(root, userMap, ownerGuid, 0);
  }

  /* ── Build full HTML ───────────────────────────────────────── */
  const html = `<!DOCTYPE html>
<html lang="${i18n.language}" dir="${i18n.dir()}">
<head>
  <meta charset="utf-8">
  <title>${esc(t('projectTeam:structure.printTitle') || 'Team Structure')} — ${esc(projectName)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 10pt;
      line-height: 1.4;
      color: #1a1a1a;
      padding: 20px 24px;
    }

    /* ── Header ── */
    .tree-header {
      border-bottom: 3px solid #1a1a1a;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .tree-header h1 {
      font-size: 16pt;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .tree-header .project-name {
      font-size: 12pt;
      font-weight: 500;
      color: #444;
    }
    .tree-meta {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      font-size: 8.5pt;
      color: #666;
    }

    /* ── Summary strip ── */
    .summary-strip {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      background: #f5f5f5;
      border-radius: 5px;
      padding: 10px 14px;
      margin-bottom: 18px;
      font-size: 9pt;
    }
    .summary-strip .stat { font-weight: 600; }
    .summary-strip .stat-label { color: #666; margin-inline-end: 3px; }

    /* ── Tree nodes ── */
    .tree-node {
      border: 1px solid #ddd;
      border-radius: 6px;
      padding: 8px 12px;
      margin-bottom: 6px;
      background: #fff;
      page-break-inside: avoid;
    }
    .tree-node.depth-0 {
      border-color: #999;
      border-width: 1.5px;
      background: #fafafa;
    }

    .node-header {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .role-label {
      font-weight: 700;
      font-size: 10pt;
    }
    .role-category {
      font-size: 8pt;
      color: #888;
      background: #f0f0f0;
      padding: 1px 6px;
      border-radius: 3px;
    }
    .owner-badge {
      font-size: 7.5pt;
      font-weight: 700;
      color: #2563eb;
      background: #dbeafe;
      padding: 1px 6px;
      border-radius: 3px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .fill-badge {
      font-size: 8pt;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 3px;
      margin-inline-start: auto;
    }
    .fill-badge.filled {
      color: #16a34a;
      background: #dcfce7;
    }
    .fill-badge.vacant {
      color: #dc2626;
      background: #fee2e2;
    }

    .node-body {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 4px;
      font-size: 9pt;
    }
    .assignee-list {
      color: #333;
      font-weight: 500;
    }
    .vacant-text {
      color: #dc2626;
      font-style: italic;
    }
    .phase-label {
      color: #888;
      font-size: 8pt;
      margin-inline-start: auto;
    }

    /* ── Connector lines (CSS borders for indented hierarchy) ── */
    .tree-node + .tree-node {
      margin-top: 4px;
    }

    /* ── Print overrides ── */
    @media print {
      body { padding: 0; font-size: 9pt; }
      .no-print { display: none !important; }
      .summary-strip { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .tree-node.depth-0 { background: #fafafa !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .fill-badge.filled { background: #dcfce7 !important; color: #16a34a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .fill-badge.vacant { background: #fee2e2 !important; color: #dc2626 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .owner-badge { background: #dbeafe !important; color: #2563eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .role-category { background: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .tree-node { page-break-inside: avoid; }
    }

    /* ── Print button ── */
    .print-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1a1a1a;
      color: #fff;
      padding: 8px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      z-index: 9999;
    }
    .print-bar button {
      background: #3b82f6;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 6px 18px;
      font-size: 10pt;
      cursor: pointer;
      font-weight: 600;
    }
    .print-bar button:hover { background: #2563eb; }
    .print-bar-spacer { height: 48px; }

    /* ── Footer ── */
    .tree-footer {
      margin-top: 24px;
      border-top: 1px solid #ccc;
      padding-top: 6px;
      font-size: 7.5pt;
      color: #999;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="print-bar no-print">
    <span>${esc(t('projectTeam:structure.printTitle') || 'Team Structure')} — ${esc(projectName)}</span>
    <button onclick="window.print()">${esc(t('projectTeam:raci.printBtn') || 'Print')}</button>
  </div>
  <div class="print-bar-spacer no-print"></div>

  <div class="tree-header">
    <h1>${esc(t('projectTeam:structure.printTitle') || 'Team Structure')}</h1>
    <div class="project-name">${esc(projectName)}</div>
    <div class="tree-meta">
      <span>${esc(dateStr)}</span>
      <span>${totalSlots} ${esc(t('projectTeam:panel.roles'))} · ${filledPositions}/${totalPositions} ${esc(t('projectTeam:panel.positionsFilled').toLowerCase())}</span>
    </div>
  </div>

  <div class="summary-strip">
    <div>
      <span class="stat-label">${esc(t('projectTeam:panel.roles'))}:</span>
      <span class="stat">${totalSlots}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:panel.positions'))}:</span>
      <span class="stat">${totalPositions}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:panel.positionsFilled'))}:</span>
      <span class="stat">${filledPositions}</span>
    </div>
    <div>
      <span class="stat-label">${esc(t('projectTeam:panel.openings'))}:</span>
      <span class="stat">${openPositions}</span>
    </div>
  </div>

  ${treeHtml}

  <div class="tree-footer">
    ${esc(t('projectTeam:raci.printFooter') || 'Generated from Life4Earth')} — ${esc(dateStr)}
  </div>
</body>
</html>`;

  /* ── Open window & trigger print ────────────────────────────── */
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // Popup blocker — fall back to blob download
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TeamStructure-${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
}
